import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

/**
 * Fetches the top 6 public trips for the homepage.
 * Only selects the fields needed by TripCard to reduce payload.
 */
export const useHomepageTrips = () => {
  return useQuery({
    queryKey: ["homepage-trips"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("trips")
        .select("id, title, countries, start_date, end_date, cover_image, trip_type, slug, score, user_id")
        .eq("is_public", true)
        .order("score", { ascending: false })
        .order("created_at", { ascending: false })
        .limit(6);

      if (error) throw error;
      return data || [];
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

/**
 * Fetches the IDs of trips liked by the current user.
 */
export const useLikedTripIds = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["liked-trip-ids", user?.id],
    queryFn: async () => {
      if (!user) return new Set<string>();

      const { data, error } = await supabase
        .from("followed_trips")
        .select("trip_id")
        .eq("user_id", user.id);

      if (error) throw error;
      return new Set(data?.map(f => f.trip_id) || []);
    },
    enabled: !!user,
    staleTime: 2 * 60 * 1000,
  });
};

/**
 * Fetches popular countries with trip counts.
 * Only selects the countries column to minimize payload.
 */
export const usePopularCountries = () => {
  return useQuery({
    queryKey: ["popular-countries"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("trips")
        .select("countries")
        .eq("is_public", true);

      if (error) throw error;

      // Aggregate countries
      const countryCount: Record<string, number> = {};
      data?.forEach((trip) => {
        trip.countries?.forEach((country: string) => {
          countryCount[country] = (countryCount[country] || 0) + 1;
        });
      });

      return Object.entries(countryCount)
        .map(([country, trip_count]) => ({ country, trip_count }))
        .sort((a, b) => b.trip_count - a.trip_count)
        .slice(0, 15);
    },
    staleTime: 5 * 60 * 1000, // 5 minutes - countries don't change often
  });
};

/**
 * Fetches only the 3 most recent blog posts with minimal fields for the homepage.
 * Excludes the full `content` field to dramatically reduce payload size.
 */
export const useRecentBlogPosts = (lang: string) => {
  return useQuery({
    queryKey: ["recent-blog-posts", lang],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("blog_posts")
        .select("id, slug, title, excerpt, category, author, image, published_at, title_en, title_es, excerpt_en, excerpt_es")
        .eq("is_published", true)
        .order("published_at", { ascending: false })
        .limit(5);

      if (error) throw error;

      // Apply localization
      return (data || []).map(post => {
        if (lang === "en" && post.title_en) {
          return { ...post, title: post.title_en, excerpt: post.excerpt_en || post.excerpt };
        }
        if (lang === "es" && post.title_es) {
          return { ...post, title: post.title_es, excerpt: post.excerpt_es || post.excerpt };
        }
        return post;
      });
    },
    staleTime: 5 * 60 * 1000,
  });
};
